import React, { useEffect, useState } from "react";
import client from "@/lib/api";
import { ImageOff } from "lucide-react";

/**
 * A GridFS-backed story asset (served from /api/stories/.../media/...) needs
 * the app's bearer token to load — a plain <img>/<video> src can't carry
 * that header, so the browser's own request goes out unauthenticated and
 * the backend correctly 401s it, rendering nothing (StoryDetail.js's Today
 * dashboard cards and the admin editor's upload previews were all hitting
 * this before this file existed). Fetch it through the authenticated axios
 * client instead and hand the element a blob URL. An external placeholder
 * URL (e.g. Unsplash, or a fresh local upload's own blob: preview) needs
 * neither and is used as-is.
 */
export function AuthedImage({ src, alt = "", className }) {
  const [resolvedSrc, setResolvedSrc] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
    if (!src) { setResolvedSrc(null); return; }
    if (!src.startsWith("/api/")) { setResolvedSrc(src); return; }
    let cancelled = false;
    let objectUrl = null;
    client.get(src.replace(/^\/api/, ""), { responseType: "blob" }).then(({ data }) => {
      if (cancelled) return;
      objectUrl = URL.createObjectURL(data);
      setResolvedSrc(objectUrl);
    }).catch((err) => {
      if (cancelled) return;
      // Previously silent — an expired token, a deleted GridFS file, and "no
      // image was ever set" all rendered as the exact same blank box, with
      // nothing in the console to tell them apart. Log the real cause.
      console.error(`AuthedImage: failed to load "${src}"`, err?.response?.status ?? err?.message ?? err);
      setResolvedSrc(null);
      setFailed(true);
    });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src]);

  if (!resolvedSrc) {
    return (
      <div className={className}>
        {failed && (
          <div className="w-full h-full flex items-center justify-center" data-testid="authed-image-failed">
            <ImageOff size={20} className="text-white/60" strokeWidth={1.5} />
          </div>
        )}
      </div>
    );
  }
  return (
    <img
      src={resolvedSrc}
      alt={alt}
      className={className}
      onError={() => {
        console.error(`AuthedImage: browser could not render "${src}"`);
        setResolvedSrc(null);
        setFailed(true);
      }}
    />
  );
}

// Same auth problem as AuthedImage, for an uploaded story video.
export function AuthedVideo({ src, className }) {
  const [resolvedSrc, setResolvedSrc] = useState(null);

  useEffect(() => {
    if (!src) { setResolvedSrc(null); return; }
    let cancelled = false;
    let objectUrl = null;
    client.get(src.replace(/^\/api/, ""), { responseType: "blob" }).then(({ data }) => {
      if (cancelled) return;
      objectUrl = URL.createObjectURL(data);
      setResolvedSrc(objectUrl);
    }).catch(() => { if (!cancelled) setResolvedSrc(null); });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src]);

  if (!resolvedSrc) return <div className={className} />;
  return <video controls className={className} src={resolvedSrc} />;
}
